import Utils from './Utils';
import { PDF_LAYOUT_KINDS, PDF_LAYOUT_TARGETS } from './pdfLayoutSettingsUtils';

const PDF_TARGETS = PDF_LAYOUT_TARGETS;

const EMAIL_HOOKS = [
    'creation',
    'activation',
    'before_expiration',
    'expiration',
    'renewal',
    'deletion',
    'reservation',
];

export function tickettype_display_name(tickettype, lang = Utils.default_lang()) {
    return Utils.localized_or_fallback(
        tickettype?.name,
        lang,
        Utils.default_lang()
    ) || tickettype?._id || '';
}

function resolve_pricing_name(name, lang) {
    if (!name)
        return null;

    if (typeof name === 'string')
        return name;

    return Utils.localized_or_fallback(name, lang, '') || null;
}

function resolve_pricing_list_name(list, lang) {
    return resolve_pricing_name(list?.name, lang) || list?._id || null;
}

function pricing_labels_from_lists(pricinglists, pricing_id, lang) {
    if (!pricinglists?.length || !pricing_id)
        return [];

    if (pricing_id.includes(':')) {
        const [pricing_list_id, pricing_key] = pricing_id.split(':');
        const list = pricinglists.find(p => p._id === pricing_list_id);
        const name = resolve_pricing_name(list?.pricings?.[pricing_key]?.name, lang);

        if (!name)
            return [];

        const listName = resolve_pricing_list_name(list, lang);
        return [listName ? `${listName} \u2192 ${name}` : name];
    }

    const labels = [];
    const seen = new Set();

    pricinglists.forEach(list => {
        const name = resolve_pricing_name(list.pricings?.[pricing_id]?.name, lang);

        if (!name)
            return;

        const listName = resolve_pricing_list_name(list, lang);
        const label = listName ? `${listName} \u2192 ${name}` : name;

        if (seen.has(label))
            return;

        seen.add(label);
        labels.push(label);
    });

    return labels;
}

export function pricing_display_names(tickettype, pricing_id, lang = Utils.default_lang(), pricinglists = null) {
    if (!pricing_id || pricing_id === 'default')
        return [];

    const from_tickettype = resolve_pricing_name(
        tickettype?.pricings?.[pricing_id]?.name,
        lang
    );

    if (from_tickettype)
        return [from_tickettype];

    if (tickettype?._id === 'one-time-pass' || pricing_id.includes(':')) {
        const from_lists = pricing_labels_from_lists(pricinglists, pricing_id, lang);

        if (from_lists.length)
            return from_lists;
    }

    return [pricing_id];
}

export function pricing_display_name(tickettype, pricing_id, lang = Utils.default_lang(), pricinglists = null) {
    const names = pricing_display_names(tickettype, pricing_id, lang, pricinglists);

    return names.length ? names.join(', ') : null;
}

/**
 * Pricings offered in a select for a ticket type.
 * One-time-pass has empty `pricings`; unique keys come from pricing lists
 * (same source as PDF / email layout pickers).
 */
export function tickettype_select_pricings(tickettype, pricinglists = []) {
    if (!tickettype)
        return [];

    if (tickettype._id === 'one-time-pass') {
        const seen = new Set();
        const result = [];

        (pricinglists || []).forEach(list => {
            Object.keys(list?.pricings || {}).forEach(key => {
                if (seen.has(key))
                    return;

                seen.add(key);
                result.push({
                    id: key,
                    name: list.pricings[key].name,
                });
            });
        });

        return result;
    }

    return Object.keys(tickettype.pricings || {}).map(id => ({
        ...(tickettype.pricings[id] || {}),
        id,
    }));
}

/**
 * Semantic UI dropdown options for one-time-pass pricings, grouped by pricing
 * list (same tree as TickettypeForm PDF / email override pickers).
 * Values stay the pricing key (not list:key), matching stored pricing_id.
 */
export function otp_pricing_dropdown_options(
    pricinglists = [],
    lang = Utils.default_lang(),
    currency = Utils.currency()
) {
    const options = [];

    (pricinglists || []).forEach((list) => {
        const keys = Object.keys(list?.pricings || {});
        if (!keys.length)
            return;

        options.push({
            key: `group-${list._id}`,
            text: list.name || list._id,
            disabled: true,
            className: 'header',
        });

        keys.forEach((key) => {
            const pricing = list.pricings[key];
            const name = Utils.localized_or_fallback(pricing?.name, lang, key);
            const price = pricing?.price?.[currency];
            const text = (price !== undefined && price !== null && price !== '')
                ? `${name} - ${price} ${currency}`
                : name;
            options.push({
                key: `${list._id}:${key}`,
                value: key,
                text,
            });
        });
    });

    return options;
}

/** Pricings that can still receive an email-layout override (OTP uses pricing lists). */
export function addable_email_override_pricings(tickettype, pricinglists = []) {
    const emails = tickettype?.opaque?.emails || {};
    return tickettype_select_pricings(tickettype, pricinglists)
        .filter(pricing => !(pricing.id in emails))
        .map(pricing => ({
            id: pricing.id,
            name: pricing.name,
        }));
}

function push_pdf_reference(references, tickettype, fields) {
    references.push({
        tickettype_id: tickettype._id,
        tickettype_name: tickettype_display_name(tickettype),
        tickettype_pricings: tickettype.pricings,
        ...fields,
    });
}

export function find_pdf_layout_references(tickettypes, layout_id) {
    const references = [];

    (tickettypes || []).forEach(tickettype => {
        const layouts = tickettype?.opaque?.layouts;
        if (!layouts)
            return;

        PDF_TARGETS.forEach(target => {
            if (layouts[target] === layout_id) {
                push_pdf_reference(references, tickettype, {
                    scope: 'default',
                    pricing_id: null,
                    target,
                });
            }
        });

        Object.keys(layouts.overrides || {}).forEach(pricing_id => {
            const override = layouts.overrides[pricing_id] || {};
            PDF_TARGETS.forEach(target => {
                if (override[target] === layout_id) {
                    push_pdf_reference(references, tickettype, {
                        scope: 'override',
                        pricing_id,
                        target,
                    });
                }
            });
        });
    });

    return references;
}

export function find_email_layout_references(tickettypes, layout_id) {
    const references = [];

    (tickettypes || []).forEach(tickettype => {
        const emails = tickettype?.opaque?.emails;
        if (!emails)
            return;

        Object.keys(emails).forEach(pricing_id => {
            const mapping = emails[pricing_id] || {};
            EMAIL_HOOKS.forEach(hook => {
                if (mapping[hook] === layout_id) {
                    references.push({
                        tickettype_id: tickettype._id,
                        tickettype_name: tickettype_display_name(tickettype),
                        tickettype_pricings: tickettype.pricings,
                        scope: pricing_id === 'default' ? 'default' : 'override',
                        pricing_id: pricing_id === 'default' ? null : pricing_id,
                        hook,
                    });
                }
            });
        });
    });

    return references;
}

export const EMAIL_LAYOUT_REF_WORKFLOW = 'workflow';
export const EMAIL_LAYOUT_REF_SETTINGS = 'settings';
export const EMAIL_LAYOUT_REF_SALEPOINT = 'salepoint';
export const EMAIL_LAYOUT_REF_APPLICATION_TYPE = 'application_type';

const APPLICATION_TYPE_EMAIL_FIELDS = [
    'on_receive_email_id',
    'on_submit_email_id',
    'on_approve_email_id',
    'on_reject_email_id',
];

function workflow_display_name(workflow, lang) {
    return Utils.localized_or_fallback(
        workflow?.name,
        lang,
        Utils.default_lang()
    ) || workflow?.id || workflow?._id || '';
}

function application_type_display_name(application_type, lang) {
    return Utils.localized_or_fallback(
        application_type?.name,
        lang,
        Utils.default_lang()
    ) || application_type?.id || '';
}

function salepoint_display_name(salepoint) {
    if (!salepoint)
        return '';

    if (typeof salepoint.name === 'string')
        return salepoint.name;

    return salepoint.name?.fr || salepoint.name?.en || salepoint._id || '';
}

/**
 * @returns {Array<{ source: string, key: string, label: string, detail?: string, link_to: string }>}
 */
export function find_workflow_email_layout_references(workflows, layout_id, lang = Utils.default_lang()) {
    const references = [];

    (workflows || []).forEach(workflow => {
        const workflow_id = workflow?.id || workflow?._id;
        if (!workflow_id)
            return;

        const matching_bricks = (workflow.bricks || []).filter(brick =>
            brick?.type === 'email.send'
            && String(brick?.options?.layout_id) === String(layout_id)
        );

        if (!matching_bricks.length)
            return;

        references.push({
            source: EMAIL_LAYOUT_REF_WORKFLOW,
            key: String(workflow_id),
            label: workflow_display_name(workflow, lang),
            link_to: `/workflows/edit/${encodeURIComponent(workflow_id)}`,
            detail_key: matching_bricks.length > 1 ? 'multiple_bricks' : null,
            detail_count: matching_bricks.length,
        });
    });

    return references.sort((a, b) => a.label.localeCompare(b.label));
}

export function find_settings_order_confirmation_reference(setting, layout_id) {
    if (String(setting?.email_layouts?.order_confirmation) !== String(layout_id))
        return [];

    return [{
        source: EMAIL_LAYOUT_REF_SETTINGS,
        key: 'settings-default',
        label_key: 'layouts.references.settings_default',
        link_to: '/settings/edit/default/default_layouts',
    }];
}

export function find_settings_refund_confirmation_reference(setting, layout_id) {
    if (String(setting?.email_layouts?.refund_confirmation) !== String(layout_id))
        return [];

    return [{
        source: EMAIL_LAYOUT_REF_SETTINGS,
        key: 'settings-default-refund',
        label_key: 'layouts.references.settings_default',
        link_to: '/settings/edit/default/default_layouts',
    }];
}

export function find_settings_gift_notification_reference(setting, layout_id) {
    if (String(setting?.email_layouts?.gift_notification) !== String(layout_id))
        return [];

    return [{
        source: EMAIL_LAYOUT_REF_SETTINGS,
        key: 'settings-default-gift',
        label_key: 'layouts.references.settings_default',
        link_to: '/settings/edit/default/default_layouts',
    }];
}

/**
 * References from settings.pdf_layouts (default layouts: tickets, OTP, receipts, …).
 *
 * @returns {Array<{
 *   source: string,
 *   key: string,
 *   label_key: string,
 *   settings_kind: string,
 *   settings_target: string,
 *   target: string,
 *   link_to: string,
 * }>}
 */
export function find_settings_pdf_layout_references(setting, layout_id) {
    const want = layout_id == null ? '' : String(layout_id);
    if (!want)
        return [];

    const references = [];
    const pdf_layouts = setting?.pdf_layouts || {};

    PDF_LAYOUT_KINDS.forEach((kind) => {
        const kind_map = pdf_layouts[kind];
        if (!kind_map || typeof kind_map !== 'object')
            return;

        PDF_TARGETS.forEach((target) => {
            if (String(kind_map[target] ?? '') !== want)
                return;

            references.push({
                source: EMAIL_LAYOUT_REF_SETTINGS,
                key: `settings-pdf-${kind}-${target}`,
                label_key: 'layouts.references.settings_pdf_default',
                settings_kind: kind,
                settings_target: target,
                target,
                link_to: '/settings/edit/default/default_layouts',
            });
        });
    });

    return references;
}

export function find_salepoints_order_confirmation_references(salepoints, layout_id) {
    return (salepoints || [])
        .filter(salepoint => String(salepoint?.settings?.email_layouts?.order_confirmation) === String(layout_id))
        .map(salepoint => ({
            source: EMAIL_LAYOUT_REF_SALEPOINT,
            key: salepoint._id,
            label_key: 'layouts.references.salepoint_order_confirmation',
            label_params: { salepoint: salepoint_display_name(salepoint) },
            link_to: `/salepoints/edit/${encodeURIComponent(salepoint._id)}`,
        }))
        .sort((a, b) => (a.label_params?.salepoint || '').localeCompare(b.label_params?.salepoint || ''));
}

export function find_application_type_email_references(application_types, layout_id, lang = Utils.default_lang()) {
    const references = [];

    (application_types || []).forEach(application_type => {
        const application_type_id = application_type?.id;
        if (!application_type_id)
            return;

        APPLICATION_TYPE_EMAIL_FIELDS.forEach(field => {
            if (String(application_type[field]) !== String(layout_id))
                return;

            references.push({
                source: EMAIL_LAYOUT_REF_APPLICATION_TYPE,
                key: `${application_type_id}:${field}`,
                label: application_type_display_name(application_type, lang),
                detail_key: `applicationTypes.${field}`,
                link_to: `/application_types/edit/${encodeURIComponent(application_type_id)}`,
            });
        });
    });

    return references.sort((a, b) => a.label.localeCompare(b.label));
}

export function find_email_layout_external_references({
    workflows,
    setting,
    salepoints,
    application_types,
    layout_id,
    lang,
}) {
    return [
        ...find_workflow_email_layout_references(workflows, layout_id, lang),
        ...find_settings_order_confirmation_reference(setting, layout_id),
        ...find_settings_refund_confirmation_reference(setting, layout_id),
        ...find_settings_gift_notification_reference(setting, layout_id),
        ...find_salepoints_order_confirmation_references(salepoints, layout_id),
        ...find_application_type_email_references(application_types, layout_id, lang),
    ];
}

const FORM_WORKFLOW_BRICK_TYPES = ['form.show', 'form.handle'];

/**
 * Workflows that reference a form via form.show / form.handle bricks.
 *
 * @returns {Array<{
 *   source: string,
 *   key: string,
 *   label: string,
 *   link_to: string,
 *   public_route: string,
 *   entity_id: string,
 *   detail_key?: string,
 *   detail_count?: number,
 * }>}
 */
export function find_workflow_form_layout_references(workflows, form_id, lang = Utils.default_lang()) {
    const references = [];
    const want = form_id == null ? '' : String(form_id);
    if (!want)
        return references;

    (workflows || []).forEach(workflow => {
        const workflow_id = workflow?.id || workflow?._id;
        if (!workflow_id)
            return;

        const matching_bricks = (workflow.bricks || []).filter(brick =>
            FORM_WORKFLOW_BRICK_TYPES.includes(brick?.type)
            && String(brick?.options?.form_id) === want
        );

        if (!matching_bricks.length)
            return;

        references.push({
            source: EMAIL_LAYOUT_REF_WORKFLOW,
            key: String(workflow_id),
            label: workflow_display_name(workflow, lang),
            link_to: `/workflows/edit/${encodeURIComponent(workflow_id)}`,
            public_route: 'workflow',
            entity_id: String(workflow_id),
            detail_key: matching_bricks.length > 1 ? 'multiple_form_bricks' : null,
            detail_count: matching_bricks.length,
        });
    });

    return references.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Missions (application types) that use this form as their registration form.
 *
 * @returns {Array<{
 *   source: string,
 *   key: string,
 *   label: string,
 *   link_to: string,
 *   public_route: string,
 *   entity_id: string,
 * }>}
 */
export function find_application_type_form_references(application_types, form_id, lang = Utils.default_lang()) {
    const references = [];
    const want = form_id == null ? '' : String(form_id);
    if (!want)
        return references;

    (application_types || []).forEach(application_type => {
        const application_type_id = application_type?.id;
        if (!application_type_id)
            return;

        if (String(application_type.form_id) !== want)
            return;

        references.push({
            source: EMAIL_LAYOUT_REF_APPLICATION_TYPE,
            key: String(application_type_id),
            label: application_type_display_name(application_type, lang),
            link_to: `/application_types/edit/${encodeURIComponent(application_type_id)}`,
            public_route: 'application',
            entity_id: String(application_type_id),
        });
    });

    return references.sort((a, b) => a.label.localeCompare(b.label));
}

export function find_form_layout_external_references({
    workflows,
    application_types,
    form_id,
    lang,
}) {
    return [
        ...find_workflow_form_layout_references(workflows, form_id, lang),
        ...find_application_type_form_references(application_types, form_id, lang),
    ];
}

export function email_layout_has_any_reference(tickettype_references, external_references) {
    return (group_layout_references_by_tickettype(tickettype_references).length > 0)
        || (external_references || []).length > 0;
}

export function group_layout_references_by_tickettype(references) {
    const groups = new Map();

    (references || []).forEach(ref => {
        if (!groups.has(ref.tickettype_id)) {
            groups.set(ref.tickettype_id, {
                tickettype_id: ref.tickettype_id,
                tickettype_name: ref.tickettype_name,
                details: [],
            });
        }

        const group = groups.get(ref.tickettype_id);
        const detail_key = [
            ref.scope,
            ref.pricing_id || '',
            ref.target || '',
            ref.hook || '',
        ].join(':');

        if (!group.details.some(d => d.key === detail_key)) {
            group.details.push({
                key: detail_key,
                scope: ref.scope,
                pricing_id: ref.pricing_id,
                target: ref.target,
                hook: ref.hook,
            });
        }
    });

    return Array.from(groups.values()).sort((a, b) =>
        a.tickettype_name.localeCompare(b.tickettype_name)
    );
}

const ONE_TIME_PASS_ID = 'one-time-pass';

/**
 * Settings defaults cover OTP via `one_time_pass` and other tickettypes via
 * `ticket`, but only when the tickettype has no opaque.layouts[target] of its
 * own (resolution: pricing override → tickettype default → settings).
 */
function is_covered_by_settings_pdf_default({
    settings_references,
    tickettype_id,
    pricing_id,
    tickettypes,
    layout_target,
}) {
    const settings_kind = tickettype_id === ONE_TIME_PASS_ID ? 'one_time_pass' : 'ticket';
    const settings_covers = (settings_references || []).some((ref) => {
        if (ref.settings_kind !== settings_kind)
            return false;

        const target = ref.settings_target || ref.target;
        return !layout_target || target === layout_target;
    });

    if (!settings_covers)
        return false;

    const tickettype = (tickettypes || []).find(tt => tt._id === tickettype_id);
    if (layout_target && tickettype?.opaque?.layouts?.[layout_target])
        return false;

    if (!pricing_id)
        return true;

    const override_layout = tickettype?.opaque?.layouts?.overrides?.[pricing_id]?.[layout_target];
    return !override_layout;
}

/**
 * Whether the PDF preview simulation (ticket type + optional pricing) is covered
 * by the layout references for this template and target.
 *
 * Tickettype opaque.layouts refs come first; when none match (typical for OTP,
 * whose defaults live in settings.pdf_layouts), settings references are checked.
 */
export function is_pdf_layout_simulation_associated({
    references,
    settings_references,
    tickettype_id,
    pricing_id,
    tickettypes,
    layout_target,
}) {
    if (!tickettype_id)
        return true;

    const tickettype_refs = (references || []).filter(ref => ref.tickettype_id === tickettype_id);

    if (!tickettype_refs.length) {
        return is_covered_by_settings_pdf_default({
            settings_references,
            tickettype_id,
            pricing_id,
            tickettypes,
            layout_target,
        });
    }

    if (!pricing_id)
        return tickettype_refs.some(ref => ref.scope === 'default');

    if (tickettype_refs.some(ref => ref.scope === 'override' && ref.pricing_id === pricing_id))
        return true;

    if (!tickettype_refs.some(ref => ref.scope === 'default'))
        return false;

    const tickettype = (tickettypes || []).find(tt => tt._id === tickettype_id);
    const override_layout = tickettype?.opaque?.layouts?.overrides?.[pricing_id]?.[layout_target];

    return !override_layout;
}

export { PDF_TARGETS, EMAIL_HOOKS };
